import { apiClient } from '../../../shared/api/client.js';

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  color?: string | null;
  postCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Post {
  id: string;
  title: string;
  subtitle?: string | null;
  slug: string;
  content: string;
  summary?: string | null;
  excerpt?: string | null;
  authorId: string;
  authorName: string;
  authorBio?: string | null;
  authorRole?: string | null;
  lineSpacing?: 'tight' | 'normal' | 'relaxed' | 'loose' | null;
  paraSpacing?: 'compact' | 'normal' | 'relaxed' | 'spacious' | null;
  status: 'draft' | 'published';
  likes: number;
  tags?: string | null;
  isFeatured?: number | null;
  category?: string | null;
  categoryId?: string | null;
  coverUrl?: string | null;
  coverImage?: string | null;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const blogApi = {
  getCategories: async (): Promise<BlogCategory[]> => {
    return apiClient('/api/blog/categories');
  },

  getCategoryById: async (id: string): Promise<BlogCategory> => {
    return apiClient(`/api/blog/categories/${id}`);
  },

  createCategory: async (data: {
    name: string;
    slug?: string;
    description?: string;
    color?: string;
  }): Promise<BlogCategory> => {
    return apiClient('/api/blog/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateCategory: async (
    id: string,
    data: {
      name?: string;
      slug?: string;
      description?: string;
      color?: string;
    }
  ): Promise<BlogCategory> => {
    return apiClient(`/api/blog/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteCategory: async (id: string): Promise<{ success: boolean; message?: string }> => {
    return apiClient(`/api/blog/categories/${id}`, {
      method: 'DELETE',
    });
  },

  getPublishedPosts: async (search?: string): Promise<Post[]> => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    return apiClient(`/api/blog?${params.toString()}`);
  },

  getPostBySlug: async (slug: string): Promise<Post> => {
    return apiClient(`/api/blog/slug/${slug}`);
  },

  getAdminPosts: async (search?: string, status?: string): Promise<Post[]> => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    return apiClient(`/api/blog/admin?${params.toString()}`);
  },

  createPost: async (data: Partial<Post>): Promise<Post> => {
    return apiClient('/api/blog', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updatePost: async (id: string, data: Partial<Post>): Promise<Post> => {
    return apiClient(`/api/blog/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deletePost: async (id: string): Promise<{ success: boolean }> => {
    return apiClient(`/api/blog/${id}`, {
      method: 'DELETE',
    });
  },
  
  likePost: async (id: string): Promise<Post> => {
    return apiClient(`/api/blog/${id}/like`, {
      method: 'POST',
    });
  },

  askAuthor: async (id: string, senderName: string, senderEmail: string, question: string): Promise<{ success: boolean }> => {
    return apiClient(`/api/blog/${id}/question`, {
      method: 'POST',
      body: JSON.stringify({ senderName, senderEmail, question }),
    });
  },
  
  subscribeNewsletter: async (email: string): Promise<{ success: boolean; message: string }> => {
    return apiClient('/api/blog/subscribe', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  getNewsletterSubscribers: async (): Promise<{ id: string; email: string; createdAt: string }[]> => {
    return apiClient('/api/blog/subscribers');
  },

  deleteNewsletterSubscriber: async (id: string): Promise<{ success: boolean }> => {
    return apiClient(`/api/blog/subscribers/${id}`, {
      method: 'DELETE',
    });
  },
};
